import { useCallback, useMemo, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { useInlineEditContext } from '../InlineEditContext';

import { useInlineAttachments } from './useInlineAttachments';

export type DcrReviewFormValues = {
	notes: string;
	isThirdPartyDataConfirmed: boolean;
};

export type DcrReviewSubmitArgs = [
	notes: string | undefined,
	thirdPartyConfirmed: boolean,
	attachmentIds: string[]
];

const EMPTY_FORM_VALUES: DcrReviewFormValues = {
	notes: '',
	isThirdPartyDataConfirmed: false
};

export const useDcrReviewForm = (open: boolean) => {
	const ctx = useInlineEditContext();

	const methods = useForm<DcrReviewFormValues>({
		defaultValues: EMPTY_FORM_VALUES,
		mode: 'onChange'
	});

	const [attachmentsExpanded, setAttachmentsExpanded] = useState(false);
	const closeReasonRef = useRef<'cancel' | 'submit'>('cancel');

	const isThirdPartyConfirmed = useWatch({
		control: methods.control,
		name: 'isThirdPartyDataConfirmed'
	});

	const {
		attachments,
		addFiles,
		removeAttachment,
		attachmentIds,
		discardAll,
		clearLocal,
		sessionId
	} = useInlineAttachments(open);

	const dcrContextValue = useMemo(
		() => ({
			entityCountryId: ctx?.entity?.countryId,
			unavailableFields: ctx?.unavailableFields ?? [],
			picklists: ctx?.picklists ?? {},
			isSimpleView: false,
			entityType: ctx?.entityType as never,
			attachmentSessionId: sessionId
		}),
		[
			ctx?.entity?.countryId,
			ctx?.entityType,
			ctx?.picklists,
			ctx?.unavailableFields,
			sessionId
		]
	);

	const buildSubmitArgs = useCallback((): DcrReviewSubmitArgs => {
		const values = methods.getValues();
		closeReasonRef.current = 'submit';
		return [
			values.notes && values.notes.trim().length > 0
				? values.notes.trim()
				: undefined,
			!!values.isThirdPartyDataConfirmed,
			attachmentIds
		];
	}, [attachmentIds, methods]);

	const markCancelled = useCallback(() => {
		closeReasonRef.current = 'cancel';
	}, []);

	const handleExited = useCallback(() => {
		methods.reset(EMPTY_FORM_VALUES);
		setAttachmentsExpanded(false);

		if (closeReasonRef.current === 'cancel') {
			discardAll();
		} else {
			clearLocal();
		}
	}, [clearLocal, discardAll, methods]);

	return {
		methods,
		isThirdPartyConfirmed,
		attachments,
		addFiles,
		removeAttachment,
		attachmentsExpanded,
		setAttachmentsExpanded,
		dcrContextValue,
		buildSubmitArgs,
		markCancelled,
		handleExited
	};
};
